import { useState, useEffect, useRef } from 'react';

interface ChannelPointsSettingsProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  rewardIds: string[];
  onRewardIdsChange: (rewardIds: string[]) => void;
}

export function ChannelPointsSettings({
  enabled,
  onEnabledChange,
  rewardIds,
  onRewardIdsChange,
}: ChannelPointsSettingsProps) {
  const [localRewardIds, setLocalRewardIds] = useState(rewardIds.join(', '));
  const localRewardIdsRef = useRef(localRewardIds);

  // Update local state if the prop changes from outside
  useEffect(() => {
    const propValue = rewardIds.join(', ');
    const normalizedLocal = localRewardIdsRef.current.split(',').map(s => s.trim()).filter(Boolean).join(', ');
    if (propValue !== normalizedLocal) {
      localRewardIdsRef.current = propValue;
      setLocalRewardIds(propValue);
    }
  }, [rewardIds]);

  const handleRewardIdsChange = (value: string) => {
    localRewardIdsRef.current = value;
    setLocalRewardIds(value);

    // Split by comma, trim whitespace, filter empty strings
    const ids = value
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id.length > 0);

    onRewardIdsChange(ids);
  };

  return (
    <div className="space-y-4">
      {/* Enable Toggle */}
      <div className="relative overflow-hidden rounded-[24px] border border-fuchsia-300/[0.14] bg-[linear-gradient(135deg,rgba(15,23,42,0.78),rgba(31,18,52,0.68))] p-4">
        <div className="pointer-events-none absolute -right-14 -top-16 h-44 w-44 rounded-full bg-fuchsia-500/14 blur-3xl" />
        <div className="relative flex items-center justify-between gap-4">
          <div>
            <div className="mb-2 text-[10px] font-black uppercase tracking-[0.22em] text-fuchsia-200/70">Channel points</div>
            <span className="text-sm font-black text-white">Respond to redemptions</span>
            <p className="mt-1 text-xs text-slate-400">StreamBuddy can react when viewers redeem Twitch channel points.</p>
          </div>
          <button
            onClick={() => onEnabledChange(!enabled)}
            className={`relative h-8 w-14 shrink-0 rounded-full transition-colors ${enabled ? 'bg-fuchsia-500 shadow-lg shadow-fuchsia-500/20' : 'bg-white/15'
              }`}
          >
            <div
              className={`absolute top-1 h-6 w-6 rounded-full bg-white transition-transform ${enabled ? 'translate-x-7' : 'translate-x-1'
                }`}
            />
          </button>
        </div>
      </div>

      {enabled && (
        <>
          {/* Reward IDs Filter */}
          <div className="space-y-3 rounded-[24px] border border-white/10 bg-black/20 p-4">
            <div>
              <p className="text-sm font-black text-white">Which rewards should trigger StreamBuddy?</p>
              <p className="mt-1 text-xs text-slate-400">
                Leave this blank to respond to every channel point redemption.
              </p>
            </div>
            <input
              type="text"
              value={localRewardIds}
              onChange={(e) => handleRewardIdsChange(e.target.value)}
              className="w-full rounded-2xl border border-white/10 bg-black/35 px-4 py-3 text-sm text-white outline-none transition-colors focus:border-fuchsia-300/60"
              placeholder="reward-id-1, reward-id-2"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
            <p className="text-xs text-slate-500">
              Find reward IDs in your Twitch Creator Dashboard under Channel Points.
            </p>
          </div>

          {/* Info Box */}
          <div className="rounded-[24px] border border-cyan-300/[0.12] bg-cyan-400/[0.04] p-4">
            <p className="text-sm font-black text-cyan-100">Current behavior</p>
            <p className="mt-2 text-sm leading-6 text-slate-300/75">
              When a viewer redeems a matching reward, StreamBuddy can acknowledge it and respond.
              {rewardIds.length === 0
                ? ' Currently set to respond to all channel point redemptions.'
                : ` Currently limited to ${rewardIds.length} specific reward${rewardIds.length === 1 ? '' : 's'}.`}
            </p>
          </div>
        </>
      )}

      {!enabled && (
        <div className="rounded-[24px] border border-dashed border-white/10 bg-white/[0.025] p-6 text-center">
          <p className="text-sm font-black text-white">Channel point responses are off</p>
          <p className="mt-2 text-xs text-slate-500">Turn this on when redemptions should trigger StreamBuddy.</p>
        </div>
      )}
    </div>
  );
}
